import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'

export interface CertificateConfig {
  name: string
  x?: number // Optional: if not provided, text will be centered
  y: number
  courseName?: string
  courseX?: number // Optional: if not provided, text will be centered
  courseY?: number
  fontSize?: number
  fontColor?: { r: number; g: number; b: number }
  fontFamily?: string
  courseFontSize?: number
  courseFontColor?: { r: number; g: number; b: number }
  enableTextWrapping?: boolean // Enable auto text wrapping for long text
  maxTextWidth?: number // Maximum width for text (percentage of page width, default: 80)
  lineHeight?: number // Line height multiplier (default: 1.2)
  topics?: Array<{ name: string }> // Optional: list of course topics to display in table
  signature?: {
    signatureImageUrl?: string // URL to signature image asset
    location?: string // Location text (default: "Bandar Lampung")
    date?: string // Date string (if not provided, will use current date)
    marginBottom?: number // Distance from bottom in pixels (default: 64)
    fontSize?: number // Font size for location and date text (default: 12)
    fontColor?: { r: number; g: number; b: number } // Font color (default: black)
    imageWidth?: number // Signature image width (default: 200)
    imageHeight?: number // Signature image height (default: 100)
  },

  // Cbt Needs
  cbtScore?: string,
  cbtScoreX?: number,
  cbtScoreY?: number,
  cbtIsPassed?: boolean,
  cbtPassingGrade?: string
  cbtPassingGradeX?: number
  cbtPassingGradeY?: number
}

export interface CertificateTemplate {
  templateUrl: string
  namePosition: {
    x?: number // Optional: if not provided, text will be centered
    y: number
  }
  coursePosition?: {
    x?: number // Optional: if not provided, text will be centered
    y: number
  }
  defaultFontSize?: number
  defaultFontColor?: { r: number; g: number; b: number }
  defaultCourseFontSize?: number
  defaultCourseFontColor?: { r: number; g: number; b: number }
}

// Default template configuration
/**
 * Split text into lines that fit within the specified width
 */
const splitTextIntoLines = (
  text: string,
  font: any,
  fontSize: number,
  maxWidth: number,
): string[] => {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (testWidth <= maxWidth) {
      currentLine = testLine
    } else {
      if (currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        // If a single word is too long, split it
        lines.push(word)
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

/**
 * Draw multi-line text with center alignment
 */
const drawMultiLineText = (
  page: any,
  text: string,
  font: any,
  fontSize: number,
  x: number,
  y: number,
  maxWidth: number,
  color: any,
  lineHeight: number = 1.2,
): number => {
  const lines = splitTextIntoLines(text, font, fontSize, maxWidth)
  const totalHeight = lines.length * fontSize * lineHeight

  // Calculate starting Y position to center the entire text block
  const startY = y + (totalHeight - fontSize) / 2

  lines.forEach((line, index) => {
    const lineWidth = font.widthOfTextAtSize(line, fontSize)
    const lineX = x + (maxWidth - lineWidth) / 2 // Center each line
    const lineY = startY - index * fontSize * lineHeight

    page.drawText(line, {
      x: lineX,
      y: lineY,
      size: fontSize,
      font,
      color,
    })
  })

  return totalHeight
}

/**
 * Draw topics table on the certificate
 */
const drawTopicsTable = async (
  page: any,
  topics: Array<{ name: string }>,
  font: any,
  fontBold: any,
  x: number,
  y: number,
  width: number,
  fontSize: number = 12,
): Promise<number> => {
  const headerHeight = 40                // slightly taller for balanced look
  const rowHeight = fontSize * 2.2       // auto-scale based on font size (was fixed 20)
  const padding = 10                     // base padding for horizontal spacing
  const headerColor = rgb(42 / 255, 170 / 255, 250 / 255)
  const textColor = rgb(0, 0, 0)
  const borderColor = rgb(0.8, 0.8, 0.8)

  // Draw header background
  page.drawRectangle({
    x: x,
    y: y - headerHeight,
    width: width,
    height: headerHeight,
    color: headerColor,
  })

  // Draw header border
  page.drawRectangle({
    x: x,
    y: y - headerHeight,
    width: width,
    height: headerHeight,
    borderColor: borderColor,
    borderWidth: 1,
  })

  // Draw header text (centered, bold look)
  const headerText = 'List of Material Studied'
  const textWidth = font.widthOfTextAtSize(headerText, fontSize + 2)
  const textX = x + (width - textWidth) / 2
  const textY = (y - headerHeight / 2 - (fontSize / 2)) + 3

  page.drawText(headerText, {
    x: textX,
    y: textY,
    size: fontSize + 2,
    fontBold,
    color: rgb(0, 0, 0),
  })

  let currentY = y - headerHeight

  // Draw each topic row
  topics.forEach((topic, index) => {
    const rowY = currentY - rowHeight * (index + 1)

    // Bigger paddings for a more open layout
    const horizontalPadding = padding * 2
    const verticalPadding = 8

    // Alternating background colors
    const rowBgColor = index % 2 === 0 ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1)
    page.drawRectangle({
      x: x,
      y: rowY,
      width: width,
      height: rowHeight,
      color: rowBgColor,
    })

    // Draw bullet point and topic text (centered vertically)
    const bulletText = `• ${topic.name}`
    const textY = rowY + (rowHeight - fontSize) / 2 + verticalPadding / 2
    page.drawText(bulletText, {
      x: x + horizontalPadding,
      y: textY,
      size: fontSize,
      font,
      color: textColor,
    })
  })

  // Draw outer border around entire table
  const totalHeight = headerHeight + topics.length * rowHeight
  page.drawRectangle({
    x: x,
    y: y - totalHeight,
    width: width,
    height: totalHeight,
    borderColor: borderColor,
    borderWidth: 1.2, // slightly thicker outer border
  })

  return totalHeight
}

/**
 * Format current date to Indonesian format (date only, no day name)
 */
const formatIndonesianDate = (date: Date): string => {
  const months = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ]

  const day = date.getDate()
  const monthName = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${monthName} ${year}`
}

/**
 * Draw signature section at the bottom of the certificate
 */
const drawSignature = async (
  page: any,
  pdfDoc: any,
  signature: NonNullable<CertificateConfig['signature']>,
  pageWidth: number,
  pageHeight: number,
  font: any,
): Promise<void> => {
  const {
    signatureImageUrl,
    location = 'Bandar Lampung',
    date,
    marginBottom = 64,
    fontSize = 12,
    fontColor = { r: 0, g: 0, b: 0 }, // Default black
    imageWidth = 200,
    imageHeight = 100,
  } = signature

  // Use provided date or format current date
  const dateText = date || formatIndonesianDate(new Date())
  const locationDateText = `${location}, ${dateText}`

  // Calculate positions - text should be above the image
  const signatureImageY = marginBottom // Image distance from bottom
  const textY = signatureImageY + imageHeight + 20 // Location/date text above the signature
  const organizerTextY = textY + fontSize + 10 // Organizer text above location/date text

  // Center positions
  const signatureX = (pageWidth - imageWidth) / 2
  const textWidth = font.widthOfTextAtSize(locationDateText, fontSize)
  const textX = (pageWidth - textWidth) / 2

  // Embed bold font for organizer text
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  // 🟩 Draw organizer label (bold, black, centered)
  const organizerText = 'Organized by PT. Jadi Praktisi Indonesia'
  const organizerTextWidth = fontBold.widthOfTextAtSize(organizerText, fontSize + 2)
  const organizerTextX = (pageWidth - organizerTextWidth) / 2
  page.drawText(organizerText, {
    x: organizerTextX,
    y: organizerTextY,
    size: fontSize + 2,
    font: fontBold,
    color: rgb(0, 0, 0), // Black
  })

  // 🟨 Draw location and date text
  page.drawText(locationDateText, {
    x: textX,
    y: textY,
    size: fontSize,
    font,
    color: rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
  })

  // 🟦 Draw signature image if provided
  if (signatureImageUrl) {
    try {
      const imageResponse = await fetch(signatureImageUrl)
      if (imageResponse.ok) {
        const imageArrayBuffer = await imageResponse.arrayBuffer()

        let image
        if (signatureImageUrl.toLowerCase().includes('.png')) {
          image = await pdfDoc.embedPng(imageArrayBuffer)
        } else if (
          signatureImageUrl.toLowerCase().includes('.jpg') ||
          signatureImageUrl.toLowerCase().includes('.jpeg')
        ) {
          image = await pdfDoc.embedJpg(imageArrayBuffer)
        } else {
          console.warn('Unsupported signature image format. Only PNG and JPG are supported.')
          return
        }

        page.drawImage(image, {
          x: signatureX,
          y: signatureImageY,
          width: imageWidth,
          height: imageHeight,
        })
      } else {
        console.warn('Failed to fetch signature image:', signatureImageUrl)
      }
    } catch (error) {
      console.warn('Error loading signature image:', error)
    }
  }
}

export const DEFAULT_CERTIFICATE_TEMPLATE: CertificateTemplate = {
  templateUrl: '/assets/certificates/template-certificate-new.pdf',
  namePosition: {
    y: 260, // Default Y position (middle of page)
  },
  coursePosition: {
    y: 360, // Default course Y position (below name)
  },
  defaultFontSize: 22,
  defaultFontColor: { r: 42, g: 170, b: 250 },
  defaultCourseFontSize: 22,
  defaultCourseFontColor: { r: 42, g: 170, b: 250 },
}

export const DEFAULT_CERTIFICATE_CBT_TEMPLATE: CertificateTemplate = {
  templateUrl: '/assets/certificates/template-certificate-cbt.pdf',
  namePosition: {
    y: 260, // Default Y position (middle of page)
  },
  coursePosition: {
    y: 360, // Default course Y position (below name)
  },
  defaultFontSize: 22,
  defaultFontColor: { r: 42, g: 170, b: 250 },
  defaultCourseFontSize: 22,
  defaultCourseFontColor: { r: 42, g: 170, b: 250 },
}

function wrapTextToLines(text: string, font: any, fontSize: number, maxWidth: number) {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (testWidth <= maxWidth) {
      currentLine = testLine
    } else {
      if (currentLine) lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  return lines
}

function measureLines(text: string, font, fontSize: number, courseMaxWidth) {
  return wrapTextToLines(text, font, fontSize, courseMaxWidth).length
}

/**
 * Generate certificate PDF with custom name and position
 */
export const generateCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<Uint8Array> => {
  try {
    const templateConfig = template || DEFAULT_CERTIFICATE_TEMPLATE

    // Debug logging
    console.log('Template config:', {
      namePosition: templateConfig.namePosition,
      coursePosition: templateConfig.coursePosition,
      defaultFontSize: templateConfig.defaultFontSize,
      defaultCourseFontSize: templateConfig.defaultCourseFontSize,
    })
    console.log('Config:', {
      name: config.name,
      courseName: config.courseName,
      y: config.y,
      courseY: config.courseY,
      enableTextWrapping: config.enableTextWrapping,
    })

    // Fetch the template PDF
    const templateResponse = await fetch(templateConfig.templateUrl)
    if (!templateResponse.ok) {
      throw new Error('Failed to fetch certificate template')
    }

    const templateArrayBuffer = await templateResponse.arrayBuffer()

    // Load the PDF document
    const pdfDoc = await PDFDocument.load(templateArrayBuffer)

    // Get the first page
    const pages = pdfDoc.getPages()
    if (pages.length === 0) {
      throw new Error('Template PDF has no pages')
    }

    const page = pages[0]
    const { width, height } = page.getSize()

    // Embed the standard font
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica)

    // Set font properties
    const fontSize = config.fontSize || templateConfig.defaultFontSize || 22
    const fontColor = config.fontColor ||
      templateConfig.defaultFontColor || { r: 42, g: 170, b: 250 }

    // Check if text wrapping is enabled
    const enableWrapping = config.enableTextWrapping !== false // Default to true
    const maxTextWidthPercent = config.maxTextWidth || 80
    const lineHeight = config.lineHeight || 1.2

    if (enableWrapping) {
      // Calculate text area for name (with margins)
      const nameMaxWidth = width * (maxTextWidthPercent / 100)
      const nameX = width * ((100 - maxTextWidthPercent) / 200) // Center the text area
      const nameY = height - (config.y || templateConfig.namePosition.y) // PDF coordinates are bottom-up

      // Add name text to the page with auto-wrapping
      const nameHeight = drawMultiLineText(
        page,
        config.name,
        fontBold,
        fontSize,
        nameX,
        nameY,
        nameMaxWidth,
        rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
        lineHeight,
      )

      // Add course name if provided
      if (config.courseName) {
        let courseFontSize =
          config.courseFontSize || templateConfig.defaultCourseFontSize || 18

        const courseFontColor = config.courseFontColor ||
          templateConfig.defaultCourseFontColor || { r: 51, g: 51, b: 51 }

        // Calculate text area for course (with margins)
        const courseMaxWidth = width * (maxTextWidthPercent / 100)
        const courseX = width * ((100 - maxTextWidthPercent) / 200) // Center the text area

        const maxLines = 2

        while (
          measureLines(config.courseName, font, courseFontSize, courseMaxWidth) > maxLines &&
          courseFontSize > 10
        ) {
          courseFontSize -= 1
        }
        
        // Position course - use config.courseY if provided, otherwise calculate from name position
        let courseY: number
        if (config.courseY !== undefined) {
          // If course Y position is explicitly set, use it
          courseY = height - config.courseY // PDF coordinates are bottom-up
          console.log(
            'Using config.courseY:',
            config.courseY,
            '-> PDF Y:',
            courseY,
          )
        } else if (templateConfig.coursePosition?.y !== undefined) {
          // If template has course Y position, use it
          courseY = height - templateConfig.coursePosition.y // PDF coordinates are bottom-up
          console.log(
            'Using template.coursePosition.y:',
            templateConfig.coursePosition.y,
            '-> PDF Y:',
            courseY,
          )
        } else {
          // Default: position course below name with some spacing
          const courseSpacing = 40 // Space between name and course
          courseY = nameY - nameHeight - courseSpacing
          console.log('Using calculated courseY from name position:', courseY)
        }

        // Add course text to the page with auto-wrapping
        const courseHeight = drawMultiLineText(
          page,
          config.courseName,
          fontBold,
          courseFontSize,
          courseX,
          courseY,
          courseMaxWidth,
          rgb(
            courseFontColor.r / 255,
            courseFontColor.g / 255,
            courseFontColor.b / 255,
          ),
          lineHeight,
        )

        // Add topics table if provided
        if (config.topics && config.topics.length > 0) {
          // Limit topics to max 6 items
          const limitedTopics = config.topics.slice(0, 6);

          const tableWidth = width * (maxTextWidthPercent / 100);
          const tableX = width * ((100 - maxTextWidthPercent) / 200); // Center the table
          const tableSpacing = -20; // Space between course name and topics table
          const tableY = courseY - courseHeight - tableSpacing;

          drawTopicsTable(
            page,
            limitedTopics,
            font,
            fontBold,
            tableX,
            tableY,
            tableWidth,
            12, // Table font size
          )
        }
      }

      // CBT Needs
      if (config.cbtScore) {
        const cbtFontSize = 18
        const cbtFontColor = { r: 0, g: 0, b: 0 } // Black
        const cbtPassingFontColor = { r: 42, g: 170, b: 250 } // Blue (same as courseTitle)
        const cbtFailColor = { r: 255, g: 0, b: 0 } // Red

        // Default positions (if not provided)
        const cbtScoreX = config.cbtScoreX || width / 2 - 100
        const cbtScoreY = height - (config.cbtScoreY || 460)

        const cbtPassingGradeX = config.cbtPassingGradeX || width / 2 - 100
        const cbtPassingGradeY = height - (config.cbtPassingGradeY || 490)

        // 🧮 Draw score
        page.drawText(`${config.cbtScore}`, {
          x: cbtScoreX,
          y: cbtScoreY,
          size: cbtFontSize - 2,
          font: fontBold,
          color: rgb(
            cbtFontColor.r / 255,
            cbtFontColor.g / 255,
            cbtFontColor.b / 255,
          ),
        })

        // 🎯 Draw passing grade (if available)
        if (config.cbtPassingGrade) {
          page.drawText(`${config.cbtPassingGrade}`, {
            x: cbtPassingGradeX,
            y: cbtPassingGradeY,
            size: cbtFontSize - 2,
            font: font,
            color: rgb(
              cbtFontColor.r / 255,
              cbtFontColor.g / 255,
              cbtFontColor.b / 255,
            ),
          })
        }

        // 🟢🟥 Draw result (PASSED / FAILED)
        if (config.cbtIsPassed !== undefined) {
          const resultText = config.cbtIsPassed ? 'PASSED' : 'FAILED'
          const resultColor = config.cbtIsPassed ? cbtPassingFontColor : cbtFailColor
          const resultFontSize = cbtFontSize + 10

          // Measure text width in the current font
          const textWidth = fontBold.widthOfTextAtSize(resultText, resultFontSize)

          // Center horizontally
          const resultX = (width - textWidth) / 2

          page.drawText(resultText, {
            x: resultX,
            y: 415,
            size: resultFontSize,
            font: fontBold,
            color: rgb(
              resultColor.r / 255,
              resultColor.g / 255,
              resultColor.b / 255,
            ),
          })
        }
      }

      // Add signature if provided (text wrapping mode)
      if (config.signature) {
        await drawSignature(page, pdfDoc, config.signature, width, height, font)
      }
    } else {
      // Fallback to original single-line text rendering
      const textWidth = font.widthOfTextAtSize(config.name, fontSize)

      // Calculate centered X position
      let x: number
      if (config.x !== undefined) {
        // If X position is explicitly set, use it
        x = config.x
      } else {
        // If no X position specified, center the text
        x = (width - textWidth) / 2
      }

      const y = height - (config.y || templateConfig.namePosition.y) // PDF coordinates are bottom-up

      // Add name text to the page
      page.drawText(config.name, {
        x,
        y,
        size: fontSize,
        font,
        color: rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
      })

      // Add course name if provided (original method)
      if (config.courseName) {
        const courseFontSize =
          config.courseFontSize || templateConfig.defaultCourseFontSize || 18
        const courseFontColor = config.courseFontColor ||
          templateConfig.defaultCourseFontColor || { r: 51, g: 51, b: 51 }

        const courseTextWidth = font.widthOfTextAtSize(
          config.courseName,
          courseFontSize,
        )

        // Calculate centered X position for course
        let courseX: number
        if (config.courseX !== undefined) {
          courseX = config.courseX
        } else {
          courseX = (width - courseTextWidth) / 2
        }

        const courseY =
          height - (config.courseY || templateConfig.coursePosition?.y || 360)

        page.drawText(config.courseName, {
          x: courseX,
          y: courseY,
          size: courseFontSize,
          font,
          color: rgb(
            courseFontColor.r / 255,
            courseFontColor.g / 255,
            courseFontColor.b / 255,
          ),
        })

        // Add topics table if provided (non-wrapping mode)
        if (config.topics && config.topics.length > 0) {
          const tableWidth = width * 0.8 // 80% of page width
          const tableX = width * 0.1 // Center the table (10% margin on each side)
          const tableSpacing = 30 // Space between course name and topics table
          const tableY = courseY - tableSpacing

          drawTopicsTable(
            page,
            config.topics,
            font,
            fontBold,
            tableX,
            tableY,
            tableWidth,
            12, // Table font size
          )
        }
      }

      // Add signature if provided (non-wrapping mode)
      if (config.signature) {
        await drawSignature(page, pdfDoc, config.signature, width, height, font)
      }
    }

    // Save the PDF
    const pdfBytes = await pdfDoc.save()
    return pdfBytes
  } catch (error) {
    console.error('Error generating certificate:', error)
    throw error
  }
}

/**
 * Download generated certificate
 */
export const downloadGeneratedCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
  customFileName?: string,
): Promise<void> => {
  try {
    const pdfBytes = await generateCertificate(config, template)

    // Create blob and download
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url

    // Generate filename with current date (date only, no day name)
    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const fileName =
      customFileName || `certificate-${config.name}-${currentDate}.pdf`

    link.download = fileName
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up
    URL.revokeObjectURL(url)

    console.log('Certificate download initiated:', fileName)
  } catch (error) {
    console.error('Error downloading certificate:', error)
    throw error
  }
}

/**
 * Preview certificate in new window/tab
 */
export const previewCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<void> => {
  try {
    const pdfBytes = await generateCertificate(config, template)

    // Create blob and open in new window
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    // Clean up after a delay to ensure the window has loaded
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 5000)

    console.log('Certificate preview opened in new window')
  } catch (error) {
    console.error('Error previewing certificate:', error)
    throw error
  }
}

/**
 * Get certificate as base64 string (useful for embedding in HTML)
 */
export const getCertificateAsBase64 = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<string> => {
  try {
    console.log('Starting base64 conversion...')
    const pdfBytes = await generateCertificate(config, template)
    console.log('PDF generated, size:', pdfBytes.length, 'bytes')

    // Convert to base64 using Blob and FileReader (safer approach)
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    console.log('Blob created, size:', blob.size, 'bytes')

    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reader.abort()
        reject(new Error('FileReader timeout'))
      }, 10000) // 10 seconds timeout

      reader.onload = () => {
        clearTimeout(timeout)
        const result = reader.result as string
        resolve(result)
      }

      reader.onerror = () => {
        clearTimeout(timeout)
        // Fallback to manual conversion if FileReader fails
        try {
          const uint8Array = new Uint8Array(pdfBytes)
          let binary = ''
          const len = uint8Array.byteLength

          // Process in smaller chunks to avoid stack overflow
          const chunkSize = 4096
          for (let i = 0; i < len; i += chunkSize) {
            const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, len))
            binary += String.fromCharCode.apply(null, Array.from(chunk))
          }

          const base64 = btoa(binary)
          resolve(`data:application/pdf;base64,${base64}`)
        } catch (fallbackError) {
          reject(new Error('Failed to convert PDF to base64'))
        }
      }

      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error converting certificate to base64:', error)
    throw error
  }
}
